import Axios from "axios"
import firebase from "firebase/compat/app"
import { getEnv } from "../../../utils/env"
import { firebaseauth } from "../../mp-database/firebaseConfig"
import storage, { AUTH_CUSTOM_TOKEN_KEY } from "./storage"

// Pentest finding #2: the qti-pwa custom token is kept in memory only, so it
// is empty after a full page reload until authGroup-qtiLogin returns. Anything
// that hands the token to qti-pwa must await getCustomToken() at the moment
// the user navigates, rather than reading it while the page renders.

const CUSTOM_TOKEN_PARAM = "customToken"
const QTI_LOGIN_TIMEOUT_MS = 15000

let pending: Promise<string | null> | null = null

const waitForSignedInUser = (): Promise<firebase.User | null> => {
  if (firebaseauth.currentUser) return Promise.resolve(firebaseauth.currentUser)

  return new Promise(resolve => {
    const unsubscribe = firebaseauth.onAuthStateChanged(user => {
      unsubscribe()
      resolve(user)
    })
  })
}

/**
 * Exchanges the signed-in user's ID token for a qti-pwa custom token and keeps
 * it in memory. Concurrent callers share the same in-flight request.
 */
export const refreshCustomToken = (idToken?: string): Promise<string | null> => {
  pending = (async () => {
    const tokenId = idToken ?? (await (await waitForSignedInUser())?.getIdToken())
    if (!tokenId) return null

    const { data } = await Axios.post(
      `${getEnv("CLOUD_FUNCTION_ROOT")}/authGroup-qtiLogin`,
      { tokenId, app: getEnv("APPLICATION_ID") },
      { timeout: QTI_LOGIN_TIMEOUT_MS }
    )
    if (!data?.success || !data.customToken) {
      throw new Error(`authGroup-qtiLogin failed: ${JSON.stringify(data)}`)
    }

    storage.set(AUTH_CUSTOM_TOKEN_KEY, data.customToken)

    return data.customToken as string
  })()

  const request = pending
  request.finally(() => {
    if (pending === request) pending = null
  }).catch(() => undefined)

  return request
}

/**
 * Returns the in-memory custom token, waiting for (or starting) the exchange if
 * needed and retrying once if it fails. Resolves to `undefined` if no token can
 * be obtained, so callers omit the customToken parameter entirely: qti-pwa then
 * falls back to the token it saved itself, whereas an empty `customToken=`
 * would skip that fallback and show "Something went wrong".
 */
export const getCustomToken = async (): Promise<string | undefined> => {
  const cached = storage.get(AUTH_CUSTOM_TOKEN_KEY)
  if (cached) return cached

  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const token = await (pending ?? refreshCustomToken())
      if (token) return token
    } catch (error) {
      console.error("[customToken] qtiLogin exchange failed", error)
    }
  }

  return undefined
}

/**
 * If `url` carries a customToken parameter (even an empty one built while the
 * token was still loading), fills it with the current token. Other URLs are
 * returned unchanged.
 */
export const withCustomToken = async (url: string): Promise<string> => {
  const parsed = new URL(url, window.location.origin)
  if (!parsed.searchParams.has(CUSTOM_TOKEN_PARAM)) return url

  const token = await getCustomToken()
  if (token) parsed.searchParams.set(CUSTOM_TOKEN_PARAM, token)
  else parsed.searchParams.delete(CUSTOM_TOKEN_PARAM)

  return parsed.toString()
}

/**
 * Plain <a href> links to qti-pwa (report table row actions, dropdown items)
 * are rendered before the token may exist. Intercept clicks on any link that
 * carries a customToken parameter and navigate with a fresh token instead.
 */
export const installCustomTokenLinkHandler = (): (() => void) => {
  const onClick = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button > 1) return

    const anchor = (event.target as Element | null)?.closest?.("a[href]")
    if (!(anchor instanceof HTMLAnchorElement)) return
    if (!new URL(anchor.href).searchParams.has(CUSTOM_TOKEN_PARAM)) return

    event.preventDefault()
    const newTab =
      event.button === 1 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      anchor.target === "_blank"

    withCustomToken(anchor.href).then(url => {
      if (newTab) window.open(url, "_blank", "noopener,noreferrer")
      else window.location.assign(url)
    })
  }

  // auxclick covers middle-click, which opens a new tab without a click event
  document.addEventListener("click", onClick, true)
  document.addEventListener("auxclick", onClick, true)

  return () => {
    document.removeEventListener("click", onClick, true)
    document.removeEventListener("auxclick", onClick, true)
  }
}
